"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const IMAGE_BASE = "/image/new-version/casting/Die-Casting/Materials-Finish";

const TOOL_STEELS = [
	{
		keyPrefix: "materialsFinishSteel1",
		image: `${IMAGE_BASE}/Materials/steel-H13.webp`,
	},
	{
		keyPrefix: "materialsFinishSteel2",
		image: `${IMAGE_BASE}/Materials/steel-2344.webp`,
	},
	{
		keyPrefix: "materialsFinishSteel3",
		image: `${IMAGE_BASE}/Materials/steel-8407.webp`,
	},
] as const;

const ALLOYS = [
	{
		keyPrefix: "materialsFinishAlloy1",
		image: `${IMAGE_BASE}/Materials/aluminum-die-casting.webp`,
		gradeCount: 7,
	},
	{
		keyPrefix: "materialsFinishAlloy2",
		image: `${IMAGE_BASE}/Materials/magnesium-die-casting.webp`,
		gradeCount: 3,
	},
	{
		keyPrefix: "materialsFinishAlloy3",
		image: `${IMAGE_BASE}/Materials/zinc-die-casting.webp`,
		gradeCount: 3,
	},
] as const;

const FINISHES = [
	{
		keyPrefix: "materialsFinishFinish1",
		image: `${IMAGE_BASE}/Surface Finish/as-cast.webp`,
	},
	{
		keyPrefix: "materialsFinishFinish2",
		image: `${IMAGE_BASE}/Surface Finish/anodizing.webp`,
	},
	{
		keyPrefix: "materialsFinishFinish3",
		image: `${IMAGE_BASE}/Surface Finish/chromate-conversion-coating.webp`,
	},
	{
		keyPrefix: "materialsFinishFinish4",
		image: `${IMAGE_BASE}/Surface Finish/powder-coating.webp`,
	},
	{
		keyPrefix: "materialsFinishFinish5",
		image: `${IMAGE_BASE}/Surface Finish/sand-blasting.webp`,
	},
	{
		keyPrefix: "materialsFinishFinish6",
		image: `${IMAGE_BASE}/Surface Finish/mechanical-Polishing.webp`,
	},
] as const;

type TFn = ReturnType<typeof useTranslations<"new-die-casting">>;

export default function DieCastingMaterialsAndFinishes() {
	const t = useTranslations("new-die-casting");
	const [activeTab, setActiveTab] = useState<"materials" | "finishes">("materials");
	const [activeSteel, setActiveSteel] = useState(0);
	const [activeAlloy, setActiveAlloy] = useState(0);

	return (
		<section className="bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-3 text-center text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
					{t("materialsFinishTitle")}
				</h2>

				<div className="mb-3 flex justify-center gap-3 sm:mb-4">
					<button
						type="button"
						onClick={() => setActiveTab("materials")}
						className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors sm:px-6 sm:text-base ${
							activeTab === "materials"
								? "border-themePrimary400 bg-themePrimary400 text-white"
								: "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
						}`}
					>
						{t("materialsFinishTabMaterials")}
					</button>
					<button
						type="button"
						onClick={() => setActiveTab("finishes")}
						className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors sm:px-6 sm:text-base ${
							activeTab === "finishes"
								? "border-themePrimary400 bg-themePrimary400 text-white"
								: "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
						}`}
					>
						{t("materialsFinishTabFinishes")}
					</button>
				</div>

				<p className="mx-auto mb-6 max-w-3xl text-center text-sm leading-relaxed text-black sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{activeTab === "materials" ? t("materialsFinishMaterialsDesc") : t("materialsFinishFinishesDesc")}
				</p>

				{activeTab === "materials" ? (
					<div className="space-y-10 sm:space-y-12 lg:space-y-16">
						<MaterialGroup
							t={t}
							titleKey="materialsFinishToolSteelTitle"
							items={TOOL_STEELS}
							activeIndex={activeSteel}
							onSelect={setActiveSteel}
						/>
						<MaterialGroup
							t={t}
							titleKey="materialsFinishAlloysTitle"
							items={ALLOYS}
							activeIndex={activeAlloy}
							onSelect={setActiveAlloy}
						/>
						<p className="text-sm leading-relaxed text-gray-700 md:text-base">
							<span className="font-semibold text-black">{t("materialsFinishNoteLabel")}</span>{" "}
							{t("materialsFinishNoteText")}
						</p>
					</div>
				) : (
					<FinishesPanel t={t} />
				)}
			</div>
		</section>
	);
}

type MaterialItem = {
	keyPrefix: string;
	image: string;
	gradeCount?: number;
};

function MaterialGroup({
	t,
	titleKey,
	items,
	activeIndex,
	onSelect,
}: {
	t: TFn;
	titleKey: string;
	items: readonly MaterialItem[];
	activeIndex: number;
	onSelect: (i: number) => void;
}) {
	const item = items[activeIndex]!;
	const gradeCount = item.gradeCount ?? 0;

	return (
		<div>
			<h3 className="mb-4 text-lg font-bold text-black sm:mb-6 sm:text-xl md:text-2xl">
				{t(titleKey as any)}
			</h3>
			<div className="mb-10 border-t-2 border-themePrimary400" />

			<div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_1fr] lg:gap-x-6">
				<div className="flex flex-row gap-0 overflow-x-auto border-b border-gray-200 lg:flex-col lg:overflow-x-visible lg:border-b-0">
					{items.map((it, i) => (
						<button
							key={it.keyPrefix}
							type="button"
							onClick={() => onSelect(i)}
							className={`whitespace-nowrap px-4 py-3 text-left text-sm font-medium transition-colors sm:py-4 sm:text-base ${
								activeIndex === i
									? "bg-themePrimary400 font-bold text-white"
									: "bg-[#f6f6f6] text-gray-700 hover:bg-gray-50"
							}`}
						>
							{t(`${it.keyPrefix}Name` as any)}
						</button>
					))}
				</div>

				<div className="flex aspect-[4/3] w-full items-center justify-center self-start">
					<div className="relative h-full w-full max-w-md">
						<Image
							src={item.image}
							alt={t(`${item.keyPrefix}ImageAlt` as any)}
							fill
							className="object-cover"
							sizes="(max-width: 1024px) 100vw, 33vw"
						/>
					</div>
				</div>

				<div className="flex flex-col justify-center p-6 sm:p-8 md:p-10">
					<h4 className="mb-3 text-lg font-bold text-black sm:mb-4 sm:text-xl md:text-2xl">
						{t(`${item.keyPrefix}Name` as any)}
					</h4>
					<p className="mb-4 text-sm leading-relaxed text-black sm:mb-6 md:text-base">
						{t(`${item.keyPrefix}Desc` as any)}
					</p>
					{gradeCount > 0 && (
						<ul className="space-y-1.5">
							{Array.from({ length: gradeCount }, (_, gi) => (
								<li key={gi} className="flex items-center gap-2 text-sm text-gray-800 md:text-base">
									<span className="inline-block size-1.5 shrink-0 rounded-full bg-gray-800" />
									{t(`${item.keyPrefix}Grade${gi + 1}` as any)}
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
		</div>
	);
}

function FinishesPanel({ t }: { t: TFn }) {
	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
			{FINISHES.map((card) => (
				<article
					key={card.keyPrefix}
					className="flex flex-col overflow-hidden rounded-md border border-[#eeeeee] bg-[#ffffff]"
				>
					<div className="relative aspect-[4/2.3] w-full bg-white">
						<Image
							src={card.image}
							alt={t(`${card.keyPrefix}ImageAlt` as any)}
							fill
							className="object-contain p-4"
							sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
						/>
					</div>
					<div className="flex flex-1 flex-col p-5 sm:p-6">
						<h3 className="mb-2 text-lg font-bold text-black sm:mb-3 sm:text-xl md:text-2xl">
							{t(`${card.keyPrefix}Title` as any)}
						</h3>
						<p className="text-sm leading-relaxed text-black md:text-base">
							{t(`${card.keyPrefix}Desc` as any)}
						</p>
					</div>
				</article>
			))}
		</div>
	);
}
